import type { BuyFormat } from '@/analytics';

/** Computed buy-in quote shown in the popup (ARC-82 §6.3). */
export interface BuyQuote {
  /** Number of $1 contracts the amount buys at the current price. */
  contracts: number;
  /** Total cost in dollars. */
  costDollars: number;
  /** Payout if the answer resolves correct — `$1 × contracts` ("To Win"). */
  toWinDollars: number;
  /** Implied probability for the chosen side, whole-number percent (0–100). */
  oddsPercent: number;
}

/**
 * Compute the live buy quote from the entered amount and current price
 * (ARC-82 §6.3). Each contract costs `priceCents / 100` dollars and pays $1 if
 * correct, so:
 *
 * - **Dollars** mode: `contracts = amount / pricePerContract`.
 * - **Contracts** mode: `contracts = amount`, `cost = contracts × pricePerContract`.
 *
 * `priceCents` is the chosen side's live price (0–100). Returns a zeroed quote
 * when the price is missing/zero so the popup degrades gracefully.
 */
export function computeBuyQuote(amount: number, format: BuyFormat, priceCents: number | null): BuyQuote {
  const safeAmount = Number.isFinite(amount) && amount > 0 ? amount : 0;
  const oddsPercent = priceCents == null ? 0 : Math.round(priceCents);

  if (priceCents == null || priceCents <= 0) {
    return { contracts: 0, costDollars: 0, toWinDollars: 0, oddsPercent };
  }

  const pricePerContract = priceCents / 100;

  if (format === 'dollars') {
    const contracts = safeAmount / pricePerContract;
    return {
      contracts,
      costDollars: safeAmount,
      toWinDollars: contracts, // $1 per contract
      oddsPercent,
    };
  }

  // contracts mode
  const contracts = safeAmount;
  return {
    contracts,
    costDollars: contracts * pricePerContract,
    toWinDollars: contracts,
    oddsPercent,
  };
}
